import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getPeriods } from '../../../../api/periods/get-periods'
import { getProducts } from '../../../../api/products/get-products'
import {
  CurrencyEnum,
  currencyEnumMapping
} from '../../../../lib/types/enums/common/currency.enum'
import { OlCommissionInput } from '../../../../lib/types/models/ol/commission/ol-commission-input'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedEnumOption } from '../../../../lib/utils/functions/misc.functions'
import { createEnumSchema } from '../../../../lib/utils/validations/enum-validation'
import {
  createNumberArraySchema,
  createNumberSchema
} from '../../../../lib/utils/validations/int-array-validation'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../../elements/inputs/controlled-multi-select-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../../elements/validation-buttons'

type Props = BaseFormProps<OlCommissionInput, true> & {
  mode: 'normal' | 'duplicate'
  className?: string
  title?: string
  hasBorderTop?: boolean
}

const formSchema: ZodType<OlCommissionInput> = z.object({
  currency: createEnumSchema({ fieldName: 'devise', enumType: CurrencyEnum }),
  durationsInDays: createNumberArraySchema('durée du séjour (nuits)', {
    isPositive: true,
    minimumNumberOfItems: 1
  }),
  productIds: createNumberArraySchema('produits', { isInteger: true, minimumNumberOfItems: 1 }),
  periodIds: createNumberArraySchema('périodes', { isInteger: true, minimumNumberOfItems: 1 }),
  fa1ePrice: createNumberSchema('prix par pax FA1E (jeunes et encadrants)', {
    isPositive: true,
    nullish: true
  }),
  fa2ePrice: createNumberSchema('prix par pax FA2E (jeunes et encadrants)', {
    isPositive: true,
    nullish: true
  }),
  advancePayment: createNumberSchema("montant de l'avance", { isPositive: true, nullish: true }),
  retainer: createNumberSchema('montant du retainer', { isPositive: true, nullish: true }),
  adminFeePerStudent: createNumberSchema('prix dépenses admin par jeune', {
    isPositive: true,
    nullish: true
  })
})

const OlCommissionForm: React.FC<Props> = ({
  mode,
  className,
  title,
  defaultValues,
  hasBorderTop,
  ...validationButtonProps
}) => {
  const form = useForm<OlCommissionInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { data: products } = useQuery({
    queryKey: ['products'],
    queryFn: () => getProducts()
  })

  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex flex-col gap-3', className)}>
          {title && (
            <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
              {title}
            </Typography>
          )}
          <FormField
            control={form.control}
            name={'productIds'}
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledMultiSelectInput
                    {...field}
                    required
                    label="Produits"
                    variant="primary"
                    searchable
                    options={
                      products?.data.map((product) => ({
                        label: product.name,
                        value: product.id
                      })) ?? []
                    }
                    selectedOptions={
                      field.value?.map((productId) => ({
                        label:
                          products?.data.find((product) => product.id === productId)?.name ?? '',
                        value: productId
                      })) ?? []
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'periodIds'}
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledMultiSelectInput
                    {...field}
                    required
                    label="Période(s)"
                    variant="warning"
                    searchable
                    options={
                      periods?.data.map((period) => ({
                        label: period.name,
                        value: period.id
                      })) ?? []
                    }
                    selectedOptions={
                      field.value?.map((periodId) => ({
                        label: periods?.data.find((period) => period.id === periodId)?.name ?? '',
                        value: periodId
                      })) ?? []
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'durationsInDays'}
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledMultiSelectInput
                    {...field}
                    required
                    type="number"
                    label="Durée du séjour (nuits)"
                    variant="grey"
                    onChange={(value) => {
                      field.onChange(value.map((val) => Number(val)))
                    }}
                    selectedOptions={
                      field.value?.map((duration) => ({
                        label: duration.toString(),
                        value: duration
                      })) ?? []
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'currency'}
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    label={'Devise'}
                    options={Object.values(CurrencyEnum).map((currency) => ({
                      label: currencyEnumMapping[currency].label,
                      value: currency
                    }))}
                    selectedOption={getSelectedEnumOption(field.value, currencyEnumMapping)}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <div
            className={cn('gap-3', {
              'grid grid-cols-2': mode === 'normal',
              'flex flex-col': mode === 'duplicate'
            })}
          >
            <FormField
              control={form.control}
              name={'fa1ePrice'}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledInput
                      {...field}
                      type="number"
                      label="Prix par pax FA1E (jeunes et encadrants)"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'fa2ePrice'}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledInput
                      {...field}
                      type="number"
                      label="Prix par pax FA2E (jeunes et encadrants)"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField
              control={form.control}
              name={'advancePayment'}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledInput {...field} type="number" label="Montant de l'avance" />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'retainer'}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledInput {...field} type="number" label="Montant du retainer" />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div
            className={cn('gap-3', {
              'grid grid-cols-2': mode === 'normal',
              'flex flex-col': mode === 'duplicate'
            })}
          >
            <FormField
              control={form.control}
              name={'adminFeePerStudent'}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledInput
                      {...field}
                      type="number"
                      label="Prix dépenses admin par jeune"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>
        <ValidationButtons hasBorderTop={hasBorderTop} {...validationButtonProps} />
      </form>
    </Form>
  )
}

export default OlCommissionForm
